import Close from "@mui/icons-material/Close";
import EditSquare from "@mui/icons-material/EditSquare";
import OpenInFull from "@mui/icons-material/OpenInFull";
import Remove from "@mui/icons-material/Remove";
import Search from "@mui/icons-material/Search";
import SupportAgent from "@mui/icons-material/SupportAgent";
import {
  Box,
  Chip,
  IconButton,
  Popover,
  type PopoverOrigin,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import ScoutIcon from "components/ScoutIcon";
import { ScoutTierChip } from "components/ScoutTierChip";
import { ScoutHost } from "generated-graphql/graphql";
import { useTenantId } from "hooks/useTenant";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { openFrontChatWithAiBotBypass } from "util/openFrontChat";

import type { ScoutAttachment } from "../scoutSubmitAttachments";
import type { ScoutThreadListItem } from "../Search";

import { ChatChrome } from "../Chat/ChatChrome";
import { ScoutChatDropzone } from "../Chat/ScoutChatDropzone";
import { DATA_CHANGE_PROPOSAL_KIND } from "../Chat/scoutDataChangeProposal";
import { ScoutHostProvider } from "../Chat/ScoutHostContext";
import { ScoutInput, type ScoutSubmitHandler } from "../Chat/ScoutInput";
import { LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND } from "../Chat/scoutLargeFileFullAnalysisProposal";
import { ScoutProposalProvider } from "../Chat/ScoutProposalContext";
import { ScoutProUpsellProvider } from "../Chat/ScoutProUpsellContext";
import { TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND } from "../Chat/scoutTenantDocumentProposal";
import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import { useScoutConversation } from "../hooks/useScoutConversation";
import { useScoutWriteMode } from "../hooks/useScoutWriteMode";
import { SCOUT_ACCESS_PACKAGES, useScoutAccessPackage } from "../scoutAccess";
import { ScoutConversationSettingsMenu } from "../ScoutConversationSettingsMenu";
import { SCOUT_SCROLLBAR_GUTTER_PX } from "../scoutLayout";
import { PopoverScoutSearch } from "./PopoverScoutSearch";
import { usePopoverContextLabel } from "./usePopoverContextLabel";
import { type ScoutPopoverSession } from "./useScoutPopoverSession";

export type PopoverScoutProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Shared session state (conversationId, openedAt) created by the caller. */
  session: ScoutPopoverSession;
  /**
   * When provided, a minimize button is shown in the header. Clicking it
   * should drop the popover into a docked/minimized state. The caller is
   * responsible for what "minimized" looks like — this just signals intent.
   */
  onMinimize?: () => void;
  /**
   * Free-form extra context to append after the auto-built page context (URL,
   * title, open time). Useful for "Explain {term}"-style triggers where the
   * caller knows the specific thing the user is asking about. The popover
   * always includes its own page-aware context — this prop only enriches it.
   */
  extraContext?: string;
  /**
   * Dismiss the popover without resetting the conversation (e.g. handoff to
   * Front support chat). Distinct from `onClose`, which also clears session.
   */
  closeScout?: () => void;
  /** MUI Popover anchorOrigin (defaults to bottom-right of the trigger). */
  anchorOrigin?: PopoverOrigin;
  /** MUI Popover transformOrigin (defaults to top-right of the popover). */
  transformOrigin?: PopoverOrigin;
  /**
   * Absolute viewport-space anchor point. When set, overrides `anchorEl` /
   * `anchorOrigin` so the popover can pin to the top-right of the visible
   * page rather than to the trigger button.
   */
  anchorPosition?: { top: number; left: number };
  /**
   * Override for the popover paper height. Accepts any CSS length string.
   * Falls back to the default clamped height when omitted.
   */
  height?: string;
  /**
   * Override for the popover paper width. Accepts any CSS length string.
   * Falls back to the default clamped width when omitted.
   */
  width?: string;
};

const POPOVER_WIDTH = 480;
const POPOVER_HEIGHT = 720;
// Reserve room for the top nav bar plus a small breathing gap so the popover
// hangs cleanly from just below the launcher without overrunning the viewport.
const POPOVER_VIEWPORT_MARGIN_PX = 96;
const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

const CONTEXT_CHIP_TOOLTIP =
  "When you open Scout this way, it always knows where you are in the application and can answer questions about anything you're currently looking at.";

function buildPopoverPageContext(input: {
  pathname: string;
  search: string;
  pageTitle: string;
  openedAt: Date;
  extra?: string;
}): string {
  const url = `${input.pathname}${input.search}`;
  const lines = [
    `Current page URL: ${url}`,
    `Current page title: ${input.pageTitle}`,
    `Popover conversation started at: ${input.openedAt.toISOString()}`,
  ];
  if (input.extra) {
    lines.push(input.extra);
  }
  return lines.join("\n");
}

/**
 * In-context Scout conversation rendered as an MUI Popover. Reuses the same
 * controller and store as the main Scout nav, so conversations started here
 * show up in the user's Scout thread list. Session state (conversationId,
 * openedAt) is held by the caller via `useScoutPopoverSession` so the popover
 * can be unmounted (e.g. minimized to a docked bar) without losing the active
 * thread. Each turn carries an auto-built page-context snapshot (URL + title
 * + open time) into the system prompt.
 */
export function PopoverScout({
  anchorEl,
  open,
  onClose,
  onMinimize,
  closeScout,
  session,
  extraContext,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
  anchorPosition,
  height,
  width,
}: PopoverScoutProps) {
  const theme = useTheme();
  const canvasColor = theme.palette.scoutCanvas;
  // `useTenantId()` prefers the URL path param (`/o/:tenantId/...` for
  // customer routes, `/staff/implementation/:tenantId` for staff) and falls
  // back to the session-storage `savedTenant`. This is what we want for staff
  // pages with no `:tenantId` in the URL — picking `user.UserTenant[0]` is
  // wrong for staff because staff users have no `UserTenant` rows. The
  // top-nav trigger hides the launcher when this is undefined, so this
  // branch is effectively the staff-with-saved-tenant case.
  const tenantId = useTenantId();
  const { scoutAccessPackage } = useScoutAccessPackage();
  const hasScoutProAccess = scoutAccessPackage === SCOUT_ACCESS_PACKAGES.PRO;
  const contextChipLabel = usePopoverContextLabel(scoutAccessPackage);
  const { pathname, search } = useLocation();
  const { conversationId, setConversationId, openedAt, setTitle, newChat } = session;

  // Which body the popover shows: the active chat, or the chat-history search
  // (recents + search) mirroring the full-page side panel.
  const [view, setView] = useState<"chat" | "search">("chat");

  const {
    conversation,
    messages,
    activeTurn,
    streamStatus,
    loading,
    isSending,
    isAborting,
    isLoadingOlderMessages,
    hasOlderMessages,
    sendMessage,
    abortTurn,
    loadOlderMessages,
    privateDocuments,
    largeDocumentOptIns,
    dismissLargeDocumentOptIn,
    confirmLargeDocumentAnalysis,
    registerComposerAttachments,
    unregisterComposerAttachment,
    dismissedScoutProUpsells,
    dismissScoutProUpsell,
    respondedTenantDocumentProposals,
    isProcessingTenantDocumentProposal,
    confirmTenantDocumentProposal,
    declineTenantDocumentProposal,
    respondedLargeFileFullAnalysisProposals,
    confirmLargeFileFullAnalysisProposal,
    declineLargeFileFullAnalysisProposal,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    respondedDataChangeProposals,
    isProcessingDataChangeProposal,
    resolveDataChangeProposals,
  } = useScoutConversation({
    tenantId,
    conversationId,
    onConversationIdChange: setConversationId,
    enablePrivateDocuments: hasScoutProAccess,
  });

  const scoutProUpsellContextValue = useMemo(
    () => ({
      dismissedUpsells: dismissedScoutProUpsells,
      onDismiss: dismissScoutProUpsell,
    }),
    [dismissedScoutProUpsells, dismissScoutProUpsell]
  );

  const isTurnRunning =
    isSending ||
    isAborting ||
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming";

  const proposalContextValue = useMemo(
    () => ({
      // Keep Accept/Reject locked until the agent turn finishes so multi-chunk
      // proposal stacks cannot be resolved before every card has been staged.
      actionsDisabled: isTurnRunning,
      handlers: {
        [TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND]: {
          respondedProposals: respondedTenantDocumentProposals,
          isProcessing: isProcessingTenantDocumentProposal,
          onConfirm: confirmTenantDocumentProposal,
          onDecline: declineTenantDocumentProposal,
        },
        [LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND]: {
          respondedProposals: respondedLargeFileFullAnalysisProposals,
          isProcessing: isConfirmingLargeDocumentAnalysis || isDecliningLargeDocumentAnalysis,
          onConfirm: confirmLargeFileFullAnalysisProposal,
          onDecline: declineLargeFileFullAnalysisProposal,
        },
        [DATA_CHANGE_PROPOSAL_KIND]: {
          respondedProposals: respondedDataChangeProposals,
          isProcessing: isProcessingDataChangeProposal,
          onResolve: resolveDataChangeProposals,
        },
      },
    }),
    [
      confirmLargeFileFullAnalysisProposal,
      confirmTenantDocumentProposal,
      declineLargeFileFullAnalysisProposal,
      declineTenantDocumentProposal,
      isConfirmingLargeDocumentAnalysis,
      isDecliningLargeDocumentAnalysis,
      isProcessingDataChangeProposal,
      isProcessingTenantDocumentProposal,
      isTurnRunning,
      resolveDataChangeProposals,
      respondedDataChangeProposals,
      respondedLargeFileFullAnalysisProposals,
      respondedTenantDocumentProposals,
    ]
  );

  // Publish the resolved title back into the (shared, persisted) session so the
  // full-page "resume conversation" chip can label itself. No-op for the
  // ephemeral inline-trigger session, which omits `setTitle`. The session is a
  // shared external store, not this component's parent — syncing via an effect
  // is the intended flow here.
  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent
    setTitle?.(conversation?.title);
  }, [conversation?.title, setTitle]);

  // Always reopen on the chat, not a stale search view, when the popover is
  // dismissed and later reopened. Adjust during render (per the React docs)
  // rather than in an effect.
  const wasOpenRef = useRef(open);
  if (wasOpenRef.current !== open) {
    wasOpenRef.current = open;
    if (!open && view !== "chat") {
      setView("chat");
    }
  }

  const handleSelectThread = useCallback(
    (thread: ScoutThreadListItem) => {
      // Load the selected thread into the popover (not the full page). Seed the
      // title optimistically for the header/chip; it re-syncs once the
      // conversation loads.
      setConversationId(thread.id);
      setTitle?.(thread.title);
      setView("chat");
    },
    [setConversationId, setTitle]
  );

  const handleNewChat = useCallback(() => {
    newChat();
    setView("chat");
  }, [newChat]);

  // Write-access toggle. Mirrors the pattern in routes/Customer/Scout/Chat:
  // local state takes precedence after the user touches it, otherwise we sync
  // from the server-resolved conversation so a resumed thread reflects the
  // value it was last sent with.
  const { writeMode, onWriteModeChange } = useScoutWriteMode({
    conversationId,
    conversationWriteMode: conversation?.writeMode,
    resetWhenConversationClears: true,
  });

  const hasMessages = messages.length > 0;
  const isLoadingConversation = Boolean(conversationId) && loading && !hasMessages;
  const showIntro = !hasMessages && !isLoadingConversation;

  const handleSubmit = useCallback<ScoutSubmitHandler>(
    async (message, options) => {
      const pageContext = buildPopoverPageContext({
        pathname,
        search,
        pageTitle: typeof document !== "undefined" ? document.title : "",
        openedAt,
        extra: extraContext,
      });
      const attachments = options?.attachments;
      await sendMessage(message, {
        pageContext,
        host: ScoutHost.Popover,
        writeMode: hasScoutProAccess ? writeMode : SCOUT_WRITE_MODE_GQL.OFF,
        attachmentIds: hasScoutProAccess
          ? attachments?.map((attachment) => attachment.privateDocumentId)
          : undefined,
        attachmentPreviews: hasScoutProAccess
          ? attachments?.map((attachment) => ({
              privateDocumentId: attachment.privateDocumentId,
              filename: attachment.filename,
              mediaType: attachment.mediaType,
              sizeBytes: attachment.sizeBytes,
              signedDownloadUrl: attachment.signedDownloadUrl,
              thumbnailSignedUrl: attachment.thumbnailSignedUrl,
              pageCount: attachment.pageCount,
            }))
          : undefined,
        extractionWatchPrivateDocumentIds: options?.extractionWatchPrivateDocumentIds,
      });
    },
    [writeMode, extraContext, hasScoutProAccess, openedAt, pathname, search, sendMessage]
  );

  const handleAbort = useCallback(() => {
    void abortTurn();
  }, [abortTurn]);

  const fullPageHref = useMemo(() => {
    if (!tenantId) {
      return undefined;
    }
    return conversationId ? `/o/${tenantId}/scout/${conversationId}` : `/o/${tenantId}/scout`;
  }, [tenantId, conversationId]);

  const scoutHostContextValue = useMemo(
    () => ({
      host: ScoutHost.Popover,
      closeScout,
    }),
    [closeScout]
  );

  return (
    <ScoutHostProvider value={scoutHostContextValue}>
      <Popover
        open={open}
        onClose={onClose}
        // MUI Popover wraps Paper in a Modal whose root spans the viewport and
        // locks body scroll, blocking every interaction with the page behind it.
        // For an in-context chat we want the user to keep scrolling, hovering,
        // and clicking the underlying page while the popover stays open — so:
        //   1. `disableScrollLock` lets the body keep scrolling.
        //   2. `hideBackdrop` removes the invisible backdrop that would have
        //      otherwise eaten clicks even with pointer-events disabled.
        //   3. `pointerEvents: "none"` on the modal root lets mouse/scroll
        //      events fall through to the page; we re-enable on the Paper so
        //      the popover itself stays fully interactive.
        // Trade-off: clicking outside the popover no longer triggers `onClose`
        // (the Modal's outside-click handler can't fire when its root ignores
        // pointer events). The minimize button, the X, and ESC remain — and
        // chat-widget patterns (Front, Intercom) already work this way.
        disableScrollLock
        hideBackdrop
        disableEnforceFocus
        slotProps={{
          root: {
            sx: { pointerEvents: "none" },
          },
          paper: {
            sx: {
              width: width ?? `min(${POPOVER_WIDTH}px, calc(100vw - 32px))`,
              height:
                height ?? `min(${POPOVER_HEIGHT}px, calc(100vh - ${POPOVER_VIEWPORT_MARGIN_PX}px))`,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              pointerEvents: "auto",
              // One uniform canvas color across header, body and context footer
              // so the popover reads as a single surface instead of a stack of
              // panels. `backgroundImage: none` removes MUI's dark-mode elevation
              // overlay (a translucent-white gradient on the elevation-8 paper) —
              // otherwise the paper paints lighter than the opaque `canvasColor`
              // we set on the Context footer, making that footer read as a darker
              // mismatched stripe.
              bgcolor: canvasColor,
              backgroundImage: "none",
              // Per-spec popover margins: 16px above the header, 40px below the
              // chat input. Horizontal 24px is applied per-section so the chat
              // area can compensate for ChatChrome's own footer padding.
              pt: 2,
              pb: 2,
            },
          },
        }}
        {...(anchorPosition
          ? {
              anchorReference: "anchorPosition" as const,
              anchorPosition,
            }
          : { anchorEl, anchorOrigin })}
        transformOrigin={transformOrigin}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, mb: 3 }}>
          <Box
            component={fullPageHref ? RouterLink : "div"}
            {...(fullPageHref ? { to: fullPageHref, onClick: onClose } : {})}
            aria-label={fullPageHref ? "Open Scout in full view" : undefined}
            sx={{
              // Hug the header content so the clickable area matches what's
              // visible rather than stretching across the empty flex space
              // between the logo and the action buttons.
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              color: "inherit",
              textDecoration: "none",
              cursor: fullPageHref ? "pointer" : "default",
            }}
          >
            <ScoutIcon sx={{ fontSize: 20 }} />
            {scoutAccessPackage ? <ScoutTierChip tier={scoutAccessPackage} /> : null}
          </Box>
          <Tooltip title={view === "search" ? "Back to chat" : "Search chats"}>
            <IconButton
              size="small"
              onClick={() => setView((current) => (current === "search" ? "chat" : "search"))}
              aria-label="Search chats"
              color={view === "search" ? "primary" : "default"}
            >
              <Search fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New chat">
            <IconButton size="small" onClick={handleNewChat} aria-label="New chat">
              <EditSquare fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }} />
          {onMinimize ? (
            <Tooltip title="Minimize">
              <IconButton size="small" onClick={onMinimize} aria-label="Minimize Scout popover">
                <Remove fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          <ScoutConversationSettingsMenu
            writeMode={hasScoutProAccess ? writeMode : SCOUT_WRITE_MODE_GQL.OFF}
            onWriteModeChange={hasScoutProAccess ? onWriteModeChange : () => undefined}
            disabled={isTurnRunning || !hasScoutProAccess}
            iconButtonSize="small"
            hasScoutProAccess={hasScoutProAccess}
          />
          <Tooltip title="Open in full Scout">
            <span>
              <IconButton
                component={RouterLink}
                to={fullPageHref ?? "#"}
                onClick={onClose}
                size="small"
                aria-label="Open in full Scout"
                disabled={!fullPageHref}
              >
                <OpenInFull fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} aria-label="Close Scout popover">
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {view === "search" ? (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <PopoverScoutSearch
              onSelectThread={handleSelectThread}
              onBack={() => setView("chat")}
            />
          </Box>
        ) : (
          // No horizontal padding here: the chat scroll surface spans the
          // popover edge-to-edge so its scrollbar bleeds to the edge (no dead
          // gap between the scrollbar and the popover). The 24px content margin
          // is carried inside ChatChrome instead — message rows via the
          // host-aware spacing hook and the composer via footerContentPaddingX.
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ScoutProUpsellProvider value={scoutProUpsellContextValue}>
              <ScoutProposalProvider value={proposalContextValue}>
                {showIntro ? (
                  <PopoverIntro
                    tenantId={tenantId}
                    conversationId={conversationId}
                    onSubmit={handleSubmit}
                    onAbort={handleAbort}
                    isSending={isTurnRunning}
                    isAborting={isAborting}
                    onAttachmentsUploaded={registerComposerAttachments}
                    onAttachmentRemoved={unregisterComposerAttachment}
                    writeMode={hasScoutProAccess ? writeMode : SCOUT_WRITE_MODE_GQL.OFF}
                    onWriteModeChange={hasScoutProAccess ? onWriteModeChange : undefined}
                    hasScoutProAccess={hasScoutProAccess}
                  />
                ) : (
                  <ChatChrome
                    tenantId={tenantId}
                    conversationId={conversationId}
                    messages={messages}
                    activeTurn={activeTurn}
                    streamStatus={streamStatus}
                    loading={isLoadingConversation}
                    initialPinned
                    onSubmit={handleSubmit}
                    onAbort={handleAbort}
                    isSending={isTurnRunning}
                    isAborting={isAborting}
                    onLoadOlderMessages={loadOlderMessages}
                    isLoadingOlderMessages={isLoadingOlderMessages}
                    hasOlderMessages={hasOlderMessages}
                    privateDocuments={privateDocuments}
                    largeDocumentOptIns={largeDocumentOptIns}
                    onDismissLargeDocumentOptIn={dismissLargeDocumentOptIn}
                    onConfirmLargeDocumentAnalysis={confirmLargeDocumentAnalysis}
                    isConfirmingLargeDocumentAnalysis={isConfirmingLargeDocumentAnalysis}
                    isDecliningLargeDocumentAnalysis={isDecliningLargeDocumentAnalysis}
                    onAttachmentsUploaded={registerComposerAttachments}
                    onAttachmentRemoved={unregisterComposerAttachment}
                    writeMode={hasScoutProAccess ? writeMode : SCOUT_WRITE_MODE_GQL.OFF}
                    onWriteModeChange={hasScoutProAccess ? onWriteModeChange : undefined}
                    hasScoutProAccess={hasScoutProAccess}
                    inputPlaceholder="Chat with Scout"
                    messagePaddingTop={0}
                    footerScrollbarGutterPx={SCOUT_SCROLLBAR_GUTTER_PX}
                    footerContentPaddingX={2}
                  />
                )}
              </ScoutProposalProvider>
            </ScoutProUpsellProvider>
          </Box>
        )}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2 }}>
          {view === "chat" ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" color="text.secondary">
                Context
              </Typography>
              <Tooltip title={CONTEXT_CHIP_TOOLTIP} placement="top-start">
                <Chip size="small" label={contextChipLabel} sx={{ cursor: "default" }} />
              </Tooltip>
            </Stack>
          ) : (
            <Box />
          )}

          <Stack direction="row" alignItems="center">
            <Tooltip title="Chat with an Encamp Representative">
              <IconButton onClick={openFrontChatWithAiBotBypass}>
                <SupportAgent fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Popover>
    </ScoutHostProvider>
  );
}

/**
 * Empty-state intro view shown before the first message. The Scout mark and
 * greeting sit at the top of the popover body; the chat input pins to the
 * bottom so it lines up just above the Context footer regardless of popover
 * height.
 */
function PopoverIntro({
  tenantId,
  conversationId,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  writeMode,
  onWriteModeChange,
  hasScoutProAccess,
}: {
  tenantId?: string;
  conversationId?: string;
  onSubmit: ScoutSubmitHandler;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  writeMode: ScoutWriteModeGql;
  onWriteModeChange?: (next: ScoutWriteModeGql) => void;
  hasScoutProAccess: boolean;
}) {
  return (
    <ScoutChatDropzone
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          alignItems: "center",
          gap: 2,
          // Matches the chat content margin now that the chat-area wrapper no
          // longer adds horizontal padding.
          px: 3,
          py: 3,
        }}
      >
        <ScoutIcon sx={{ width: 56, height: 56 }} />
        <Typography variant="subtitle1" textAlign="center">
          Hey there! I&apos;m Scout, your Encamp guide. How can I help you today?
        </Typography>
        <Box sx={{ width: "100%", mt: "auto" }}>
          <ScoutInput
            tenantId={tenantId}
            conversationId={conversationId}
            onSubmit={onSubmit}
            onAbort={onAbort}
            isSending={isSending}
            isAborting={isAborting}
            onAttachmentsUploaded={onAttachmentsUploaded}
            onAttachmentRemoved={onAttachmentRemoved}
            placeholder="Chat with Scout"
            writeMode={writeMode}
            onWriteModeChange={onWriteModeChange}
            hasScoutProAccess={hasScoutProAccess}
          />
        </Box>
      </Stack>
    </ScoutChatDropzone>
  );
}
